import { useState, useEffect, useRef, useCallback } from "react";
import { usePropertyFilter } from "@/context/PropertyFilter";
import api from "@/lib/api";
import OnboardingBanner from "@/components/OnboardingBanner";
import PricingAlertsCard from "@/components/PricingAlertsCard";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lightning, Buildings, CurrencyInr,
  ArrowUp, ArrowDown, CalendarBlank, CheckCircle,
  Sparkle, Warning, UploadSimple, X, Database, Globe,
  ChartBar, TrendUp, Plus, Tag,
} from "@phosphor-icons/react";
import { toast } from "sonner";

// ── Seasonal Rules Panel ──────────────────────────────────────────────────────
function SeasonalRulesPanel({ rules, onChange, onSave, saving }) {
  const [showForm, setShowForm] = useState(false);
  const [newRule, setNewRule] = useState({ name: "", start_mmdd: "", end_mmdd: "", adjustment_pct: 15, active: true });

  const addRule = () => {
    const mmdd = /^\d{2}-\d{2}$/;
    if (!newRule.name.trim()) { toast.error("Enter a rule name"); return; }
    if (!mmdd.test(newRule.start_mmdd) || !mmdd.test(newRule.end_mmdd)) {
      toast.error("Dates must be MM-DD format e.g. 10-15"); return;
    }
    onChange([...rules, { ...newRule, id: Math.random().toString(36).slice(2, 10) }]);
    setNewRule({ name: "", start_mmdd: "", end_mmdd: "", adjustment_pct: 15, active: true });
    setShowForm(false);
  };

  const removeRule = id => onChange(rules.filter(r => r.id !== id));
  const toggleRule = id => onChange(rules.map(r => r.id === id ? { ...r, active: !r.active } : r));

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm overflow-hidden">
      <div className="px-5 py-3.5 border-b border-slate-50 dark:border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Tag size={15} weight="duotone" className="text-amber-500" />
          <h3 className="font-semibold text-sm text-slate-800 dark:text-white">Seasonal Pricing Rules</h3>
          {rules.filter(r => r.active).length > 0 && (
            <span className="text-[10px] font-bold px-2 py-0.5 bg-amber-50 text-amber-600 border border-amber-200 rounded-full">
              {rules.filter(r => r.active).length} active
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {rules.length > 0 && (
            <button onClick={onSave} disabled={saving}
              className="flex items-center gap-1 text-xs px-3 py-1.5 bg-amber-400 hover:bg-amber-500 text-white font-semibold rounded-xl transition disabled:opacity-50">
              {saving
                ? <div className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                : "Save rules"}
            </button>
          )}
          <button onClick={() => setShowForm(v => !v)}
            className="flex items-center gap-1 text-xs px-3 py-1.5 bg-slate-50 hover:bg-slate-100 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 font-semibold rounded-xl border border-slate-200 dark:border-slate-600 transition">
            <Plus size={11} weight="bold" /> Add Rule
          </button>
        </div>
      </div>

      <div className="px-5 py-3 space-y-1">
        {rules.length === 0 && !showForm && (
          <p className="text-xs text-slate-400 text-center py-4">
            No seasonal rules yet. Add rules to auto-adjust rates for Diwali, IPL season, monsoon off-peak, or any custom period.
          </p>
        )}

        {rules.map(rule => (
          <div key={rule.id}
            className={`flex items-center gap-3 py-2.5 border-b border-slate-50 dark:border-slate-700/50 last:border-0 ${!rule.active ? "opacity-50" : ""}`}>
            <button onClick={() => toggleRule(rule.id)} title={rule.active ? "Disable" : "Enable"}
              className={`shrink-0 w-4 h-4 rounded-full border-2 transition ${rule.active ? "bg-amber-400 border-amber-400" : "border-slate-300 dark:border-slate-600"}`} />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">{rule.name}</p>
              <p className="text-[10px] text-slate-400 font-mono">{rule.start_mmdd} → {rule.end_mmdd}</p>
            </div>
            <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full shrink-0 ${rule.adjustment_pct >= 0 ? "bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400" : "bg-red-50 text-red-500 dark:bg-red-900/30 dark:text-red-400"}`}>
              {rule.adjustment_pct >= 0 ? "+" : ""}{rule.adjustment_pct}%
            </span>
            <button onClick={() => removeRule(rule.id)} className="text-slate-300 hover:text-red-400 transition shrink-0">
              <X size={13} weight="bold" />
            </button>
          </div>
        ))}

        {showForm && (
          <div className="mt-2 pt-3 border-t border-slate-100 dark:border-slate-700 space-y-3">
            <input
              placeholder="Rule name (e.g. Diwali Season, IPL Finals, Monsoon Discount)"
              value={newRule.name} onChange={e => setNewRule(r => ({ ...r, name: e.target.value }))}
              className="w-full px-3 py-2 text-xs rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-400" />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-slate-400 block mb-1">From (MM-DD)</label>
                <input placeholder="10-15" maxLength={5} value={newRule.start_mmdd}
                  onChange={e => setNewRule(r => ({ ...r, start_mmdd: e.target.value }))}
                  className="w-full px-3 py-1.5 text-xs font-mono rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-400" />
              </div>
              <div>
                <label className="text-[10px] text-slate-400 block mb-1">To (MM-DD)</label>
                <input placeholder="10-25" maxLength={5} value={newRule.end_mmdd}
                  onChange={e => setNewRule(r => ({ ...r, end_mmdd: e.target.value }))}
                  className="w-full px-3 py-1.5 text-xs font-mono rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-400" />
              </div>
            </div>
            <div>
              <label className="text-[10px] text-slate-400 block mb-1">Rate adjustment (negative = discount)</label>
              <div className="flex items-center gap-3">
                <input type="range" min={-50} max={100} step={5} value={newRule.adjustment_pct}
                  onChange={e => setNewRule(r => ({ ...r, adjustment_pct: Number(e.target.value) }))}
                  className="flex-1 accent-amber-400 h-1.5" />
                <span className={`text-sm font-bold w-12 text-right ${newRule.adjustment_pct >= 0 ? "text-green-600" : "text-red-500"}`}>
                  {newRule.adjustment_pct >= 0 ? "+" : ""}{newRule.adjustment_pct}%
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={addRule}
                className="flex-1 py-1.5 bg-amber-400 hover:bg-amber-500 text-white text-xs font-semibold rounded-xl transition">
                Add Rule
              </button>
              <button onClick={() => setShowForm(false)}
                className="px-4 py-1.5 text-xs text-slate-500 hover:text-slate-700 transition">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DirectionBadge({ direction }) {
  if (direction === "increase") return (
    <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-50 text-green-600 border border-green-200">
      <ArrowUp size={9} weight="bold" /> INCREASE
    </span>
  );
  if (direction === "decrease") return (
    <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200">
      <ArrowDown size={9} weight="bold" /> DECREASE
    </span>
  );
  return (
    <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-50 text-slate-400 border border-slate-200">
      MAINTAIN
    </span>
  );
}

function OccupancyBar({ pct }) {
  const color = pct >= 80 ? "bg-green-400" : pct >= 50 ? "bg-amber-400" : "bg-red-400";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className={`h-full rounded-full ${color}`} />
      </div>
      <span className="text-[11px] font-semibold text-slate-600 dark:text-slate-200 w-8 text-right">{pct}%</span>
    </div>
  );
}

function PropertyPricingCard({ property, onRateUpdate, onMinMaxUpdate, autoSuggest }) {
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading]       = useState(false);
  const [editRate, setEditRate]     = useState(false);
  const [newRate, setNewRate]       = useState(property.nightly_rate || "");
  const [saving, setSaving]         = useState(false);
  const [localMin, setLocalMin]     = useState(property.min_price || "");
  const [localMax, setLocalMax]     = useState(property.max_price || "");
  const [savingGuards, setSavingGuards] = useState(false);
  const [discountPct, setDiscountPct]   = useState(15);
  const [applyingDisc, setApplyingDisc] = useState(false);
  const triggered = useRef(false);

  const getSuggestion = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/pricing/suggest", {
        property_id: property.property_id || property.id,
      });
      setSuggestion(data);
    } catch (err) { toast.error(err.response?.data?.detail || err.message || "AI suggestion failed"); }
    finally { setLoading(false); }
  }, [property.property_id, property.id]);

  useEffect(() => {
    if (autoSuggest && !triggered.current) {
      triggered.current = true;
      getSuggestion();
    }
  }, [autoSuggest, getSuggestion]);

  const saveRate = async () => {
    const rate = Number(newRate);
    if (!rate || rate <= 0) { toast.error("Enter a valid rate"); return; }
    setSaving(true);
    try {
      await api.put(`/properties/${property.property_id || property.id}`, { nightly_rate: rate });
      onRateUpdate(property.property_id || property.id, rate);
      setEditRate(false);
      toast.success("Rate updated");
    } catch { toast.error("Failed to update rate"); }
    finally { setSaving(false); }
  };

  const applyRate = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/pricing/apply", {
        property_id:  property.property_id || property.id,
        nightly_rate: suggestion.suggested_rate,
        rate_calendar: suggestion.rate_calendar || [],
      });
      onRateUpdate(property.property_id || property.id, suggestion.suggested_rate);
      const msg = data.aiosell_pushed
        ? `₹${suggestion.suggested_rate?.toLocaleString("en-IN")}/night applied · Pushed to all OTAs ✓`
        : `Rate updated to ₹${suggestion.suggested_rate?.toLocaleString("en-IN")}/night`;
      toast.success(msg);
      setSuggestion(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to apply rate"); }
    finally { setSaving(false); }
  };

  const applyDiscount = async () => {
    setApplyingDisc(true);
    try {
      const pid = property.property_id || property.id;
      // 0% means "put this day back to the full rate" — clear the override instead.
      if (discountPct === 0) {
        const today = new Date();
        const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
        const { data } = await api.post("/pricing/discount/remove", { property_id: pid, date: dateStr });
        if (!data.removed) {
          toast(data.message || "No discount is set for today");
        } else {
          toast.success(
            `Discount removed for today → back to ₹${data.rate?.toLocaleString("en-IN")}/night` +
            (data.pushed ? " · pushed to OTAs ✓" : data.routed ? " · sent to the pricing engine, booking sites update once it is applied" : "")
          );
        }
        return;
      }
      const { data } = await api.post("/pricing/discount", {
        property_id: pid, discount_pct: discountPct, horizon_days: 1,
      });
      if (!data.nights) {
        toast(data.message || "Today is already booked — nothing to discount");
      } else {
        toast.success(
          `${discountPct}% off applied for today → ₹${data.rate?.toLocaleString("en-IN")}/night` +
          (data.pushed ? " · pushed to OTAs ✓" : data.routed ? " · sent to the pricing engine, booking sites update once it is applied" : "")
        );
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to apply discount"); }
    finally { setApplyingDisc(false); }
  };

  const saveGuards = async () => {
    const pid = property.property_id || property.id;
    setSavingGuards(true);
    try {
      await api.put(`/properties/${pid}`, {
        min_price: localMin ? Number(localMin) : 0,
        max_price: localMax ? Number(localMax) : 0,
      });
      if (onMinMaxUpdate) onMinMaxUpdate(pid, Number(localMin) || 0, Number(localMax) || 0);
      toast.success("Price guards saved");
    } catch { toast.error("Failed to save price guards"); }
    finally { setSavingGuards(false); }
  };

  const occupancy = property.occupancy_pct ?? 0;
  const currentRate = property.nightly_rate || 0;

  return (
    <motion.div layout initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm overflow-hidden">

      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-50 dark:border-slate-700 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center shrink-0">
            <Buildings size={17} weight="duotone" className="text-amber-500" />
          </div>
          <div>
            <p className="font-semibold text-slate-800 dark:text-white text-sm leading-snug">{property.name}</p>
            {property.location && <p className="text-[11px] text-slate-400 mt-0.5">{property.location}</p>}
          </div>
        </div>
        <button onClick={getSuggestion} disabled={loading}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-amber-400 hover:bg-amber-500 text-white font-semibold rounded-xl transition disabled:opacity-50 shrink-0">
          {loading
            ? <div className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            : <><Sparkle size={12} weight="fill" /> AI Suggest</>}
        </button>
      </div>

      {/* Stats */}
      <div className="px-5 py-4 grid grid-cols-2 gap-4 border-b border-slate-50 dark:border-slate-700">
        <div>
          <p className="text-[11px] text-slate-400 mb-1">Current Nightly Rate</p>
          {editRate ? (
            <div className="flex items-center gap-1.5">
              <CurrencyInr size={15} className="text-slate-400" />
              <input autoFocus type="number" value={newRate} onChange={e => setNewRate(e.target.value)}
                className="w-24 px-2 py-1 text-sm font-bold rounded-lg border border-amber-300 bg-amber-50 focus:outline-none" />
              <button onClick={saveRate} disabled={saving}
                className="text-[10px] px-2 py-1 bg-amber-400 text-white rounded-lg font-semibold">Save</button>
              <button onClick={() => setEditRate(false)} className="text-[10px] text-slate-400">✕</button>
            </div>
          ) : (
            <button onClick={() => { setNewRate(currentRate); setEditRate(true); }}
              className="flex items-center gap-1 group">
              <span className="text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center gap-0.5">
                <CurrencyInr size={16} weight="bold" />
                {currentRate.toLocaleString("en-IN")}
              </span>
              <span className="text-[10px] text-slate-300 group-hover:text-amber-400 transition ml-1">edit</span>
            </button>
          )}
        </div>
        <div>
          <p className="text-[11px] text-slate-400 mb-1">This Month Occupancy</p>
          <OccupancyBar pct={occupancy} />
        </div>

        {/* Price guards */}
        <div className="col-span-2 pt-3 border-t border-slate-50 dark:border-slate-700/60">
          <p className="text-[11px] text-slate-400 mb-1.5">Price Guards (floor / ceiling)</p>
          <div className="flex items-center gap-2">
            <input type="number" placeholder="Min ₹" value={localMin}
              onChange={e => setLocalMin(e.target.value)}
              className="w-24 px-2 py-1 text-xs rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-300" />
            <span className="text-slate-300 text-xs">–</span>
            <input type="number" placeholder="Max ₹" value={localMax}
              onChange={e => setLocalMax(e.target.value)}
              className="w-24 px-2 py-1 text-xs rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-300" />
            <button onClick={saveGuards} disabled={savingGuards}
              className="px-3 py-1 text-xs bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 font-semibold rounded-lg transition disabled:opacity-50">
              {savingGuards ? "…" : "Set"}
            </button>
          </div>
        </div>

        {/* Quick discount slider — apply a promo to today's open night */}
        <div className="col-span-2 pt-3 border-t border-slate-50 dark:border-slate-700/60">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[11px] text-slate-400">Quick Discount · today only</p>
            <span className={`text-xs font-bold ${discountPct === 0 ? "text-slate-500 dark:text-slate-300" : "text-emerald-600 dark:text-emerald-400"}`}>
              {discountPct === 0 ? "No discount" : `−${discountPct}%`}
            </span>
          </div>
          <input type="range" min="0" max="40" step="1" value={discountPct}
            onChange={e => setDiscountPct(Number(e.target.value))}
            className="w-full accent-black dark:accent-white cursor-pointer" />
          <div className="flex items-end justify-between mt-2">
            <div className="leading-tight">
              {(() => {
                const orig = property.nightly_rate || 0;
                const floor = Number(property.min_price) || 0;
                // Manual discounts are deliberate and are NOT capped at the price floor;
                // that floor only guards the automated pricing engines.
                const newRate = Math.max(1, Math.round(orig * (1 - discountPct / 100)));
                const belowFloor = floor > 0 && newRate < floor;
                if (discountPct === 0) {
                  return (
                    <>
                      <span className="text-lg font-bold text-slate-700 dark:text-slate-200">₹{orig.toLocaleString("en-IN")}</span>
                      <span className="text-[11px] text-slate-400">/night</span>
                      <span className="block text-[10px] text-slate-400">clears today's discount</span>
                    </>
                  );
                }
                return (
                  <>
                    <span className="text-xs text-slate-400 line-through mr-1.5">₹{orig.toLocaleString("en-IN")}</span>
                    <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">₹{newRate.toLocaleString("en-IN")}</span>
                    <span className="text-[11px] text-slate-400">/night</span>
                    {floor ? (
                      <span className="block text-[10px] text-slate-400">
                        {belowFloor
                          ? `below floor ₹${floor.toLocaleString("en-IN")} · floor still guards automatic pricing`
                          : `floor ₹${floor.toLocaleString("en-IN")}`}
                      </span>
                    ) : null}
                  </>
                );
              })()}
            </div>
            <button onClick={applyDiscount} disabled={applyingDisc}
              className={`px-3.5 py-1.5 text-xs text-white font-semibold rounded-lg transition disabled:opacity-50 shrink-0 ${
                discountPct === 0 ? "bg-red-500 hover:bg-red-600" : "bg-emerald-500 hover:bg-emerald-600"}`}>
              {applyingDisc ? "…" : discountPct === 0 ? "Remove discount" : "Apply discount"}
            </button>
          </div>
        </div>
      </div>

      {/* AI Suggestion */}
      <AnimatePresence>
        {suggestion && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }}
            className="px-5 py-4 space-y-3 bg-gradient-to-b from-amber-50/40 to-transparent dark:from-amber-900/10">

            {suggestion.auto_applied && (
              <div className="flex items-center gap-2 text-xs text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl px-3 py-2 font-semibold">
                <CheckCircle size={13} weight="fill" className="text-green-500 shrink-0" />
                Rate auto-applied — property now priced at ₹{suggestion.suggested_rate?.toLocaleString("en-IN")}/night
              </div>
            )}

            {/* Data sources used */}
            <div className="flex gap-2 flex-wrap">
              {suggestion.market_data_used && (
                <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100 font-medium">
                  <Globe size={9} weight="fill" /> Live market data
                </span>
              )}
              {suggestion.historical_months > 0 && (
                <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-600 border border-violet-100 font-medium">
                  <Database size={9} weight="fill" /> {suggestion.historical_months} months of your history
                </span>
              )}
              {!suggestion.market_data_used && suggestion.historical_months === 0 && (
                <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-slate-50 text-slate-400 border border-slate-200 font-medium">
                  <Warning size={9} weight="fill" /> Based on AI general knowledge only
                </span>
              )}
            </div>

            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 dark:text-slate-400">Suggested Rate</span>
                <span className="text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-0.5">
                  <CurrencyInr size={18} weight="bold" />
                  {suggestion.suggested_rate?.toLocaleString("en-IN")}
                </span>
                {suggestion.change_pct !== undefined && (
                  <span className={`text-xs font-semibold ${suggestion.change_pct > 0 ? "text-green-600" : suggestion.change_pct < 0 ? "text-red-500" : "text-slate-500"}`}>
                    {suggestion.change_pct > 0 ? "+" : ""}{suggestion.change_pct}%
                  </span>
                )}
              </div>
              <DirectionBadge direction={suggestion.direction} />
            </div>

            {suggestion.weekend_premium > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-violet-600 bg-violet-50 border border-violet-100 rounded-lg px-3 py-2">
                <span>Weekend premium: +<CurrencyInr size={11} weight="bold" className="inline" />{suggestion.weekend_premium?.toLocaleString("en-IN")}/night</span>
              </div>
            )}

            {suggestion.seasonal_note && (
              <div className="flex items-start gap-2 text-xs text-violet-700 bg-violet-50 border border-violet-100 rounded-lg px-3 py-2">
                <CalendarBlank size={12} className="shrink-0 mt-0.5" weight="bold" />
                <span>{suggestion.seasonal_note}</span>
              </div>
            )}

            {suggestion.reasoning && (
              <p className="text-xs text-slate-600 dark:text-slate-200 leading-relaxed bg-white dark:bg-slate-700/50 rounded-xl border border-slate-100 dark:border-slate-600 p-3">
                {suggestion.reasoning}
              </p>
            )}

            {suggestion.tips?.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">AI Tips</p>
                {suggestion.tips.map((tip, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-200">
                    <CheckCircle size={12} weight="fill" className="text-amber-400 mt-0.5 shrink-0" />
                    <span>{tip}</span>
                  </div>
                ))}
              </div>
            )}

            {suggestion.market_benchmark && (
              <MarketBenchmark benchmark={suggestion.market_benchmark} />
            )}

            {suggestion.rate_calendar?.length > 0 && (
              <RateCalendar entries={suggestion.rate_calendar} />
            )}

            <button onClick={applyRate} disabled={saving}
              className="w-full py-2 bg-amber-400 hover:bg-amber-500 text-white text-sm font-semibold rounded-xl transition disabled:opacity-50 flex items-center justify-center gap-2">
              {saving
                ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                : <><Lightning size={14} weight="fill" /> Apply Suggested Rate</>}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

const RATE_TYPE_STYLES = {
  base:        { bg: "bg-slate-100 dark:bg-slate-700",           text: "text-slate-700 dark:text-slate-200",  dot: "bg-slate-400",   label: "Base"       },
  weekend:     { bg: "bg-amber-50 dark:bg-amber-900/30",         text: "text-amber-700 dark:text-amber-300",  dot: "bg-amber-400",   label: "Weekend"    },
  event:       { bg: "bg-violet-50 dark:bg-violet-900/30",       text: "text-violet-700 dark:text-violet-300",dot: "bg-violet-500",  label: "Event"      },
  last_minute: { bg: "bg-red-50 dark:bg-red-900/20",             text: "text-red-600 dark:text-red-400",      dot: "bg-red-400",     label: "Last-min"   },
  gap_fill:    { bg: "bg-blue-50 dark:bg-blue-900/20",           text: "text-blue-600 dark:text-blue-400",    dot: "bg-blue-400",    label: "Gap fill"   },
  booked:      { bg: "bg-slate-200/60 dark:bg-slate-600/40",     text: "text-slate-400 dark:text-slate-500",  dot: "bg-slate-300",   label: "Booked"     },
};

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAY_HEADERS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

function buildCalendarGrid(entries) {
  const months = {};
  for (const e of entries) {
    const mk = e.date.slice(0, 7);
    if (!months[mk]) months[mk] = [];
    months[mk].push(e);
  }
  return Object.entries(months).map(([mk, mes]) => {
    const [yr, mo] = mk.split("-").map(Number);
    const weeks = [];
    let row = new Array(7).fill(null);
    for (const e of mes) {
      const dow = new Date(e.date + "T00:00:00").getDay();
      row[dow] = e;
      if (dow === 6) { weeks.push(row); row = new Array(7).fill(null); }
    }
    if (row.some(x => x !== null)) weeks.push(row);
    return { label: `${MONTH_NAMES[mo - 1]} ${yr}`, weeks };
  });
}

function RateCalendar({ entries }) {
  if (!entries?.length) return null;
  const grid = buildCalendarGrid(entries);
  const usedTypes = new Set(entries.map(e => e.type).filter(Boolean));
  const legendTypes = ["base", "weekend", "event", "last_minute", "gap_fill", "booked"];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">30-Day Rate Calendar</p>
        <div className="flex gap-3 flex-wrap">
          {legendTypes.filter(t => usedTypes.has(t)).map(t => (
            <span key={t} className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
              <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${RATE_TYPE_STYLES[t].dot}`} />
              {RATE_TYPE_STYLES[t].label}
            </span>
          ))}
        </div>
      </div>

      {grid.map(({ label, weeks }) => (
        <div key={label} className="space-y-1.5">
          <p className="text-sm font-bold text-slate-700 dark:text-slate-200">{label}</p>
          <div className="grid grid-cols-7 gap-1.5">
            {DAY_HEADERS.map(d => (
              <div key={d} className="text-center text-[11px] font-semibold text-slate-400 dark:text-slate-500 pb-1">{d}</div>
            ))}
          </div>
          {weeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7 gap-1.5">
              {week.map((entry, di) => {
                if (!entry) return <div key={di} />;
                const style = RATE_TYPE_STYLES[entry.type] || RATE_TYPE_STYLES.base;
                const d = new Date(entry.date + "T00:00:00");
                return (
                  <div key={di}
                    title={`${entry.date}${entry.modifiers?.length ? " · " + entry.modifiers.join(", ") : ""}${entry.discount_pct ? ` · -${entry.discount_pct}%` : ""}`}
                    className={`rounded-xl flex flex-col items-center justify-center gap-0.5 py-2 min-h-[64px] ${style.bg} cursor-default select-none`}>
                    <span className={`text-xs font-bold ${style.text}`}>{d.getDate()}</span>
                    {entry.rate != null ? (
                      <span className={`text-[11px] font-bold ${style.text} leading-tight`}>
                        ₹{entry.rate >= 1000 ? (entry.rate / 1000).toFixed(1) + "k" : entry.rate}
                      </span>
                    ) : (
                      <span className="text-[10px] text-slate-400 dark:text-slate-500 font-medium">Booked</span>
                    )}
                    {entry.discount_pct && (
                      <span className="text-[10px] text-red-500 dark:text-red-400 font-bold leading-none">-{entry.discount_pct}%</span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function MarketBenchmark({ benchmark }) {
  if (!benchmark) return null;
  return (
    <div className="flex items-start gap-2 rounded-xl border border-teal-100 dark:border-teal-800/40 bg-teal-50 dark:bg-teal-900/10 px-3 py-2.5">
      <TrendUp size={14} className="text-teal-500 mt-0.5 shrink-0" weight="bold" />
      <div className="text-[11px] text-teal-800 dark:text-teal-300 space-y-0.5">
        <p className="font-semibold">Market benchmark · {benchmark.city_display || ""}</p>
        <p>
          Avg occupancy <strong>{benchmark.avg_occupancy_pct}%</strong> · Avg daily rate <strong>₹{Number(benchmark.avg_adr_inr).toLocaleString("en-IN")}</strong>
          <span className="text-teal-600 dark:text-teal-400 ml-1">({benchmark.source})</span>
        </p>
      </div>
    </div>
  );
}

function CsvImportModal({ properties, onClose, onImported }) {
  const [file, setFile]           = useState(null);
  const [propId, setPropId]       = useState(properties[0]?.property_id || properties[0]?.id || "");
  const [uploading, setUploading] = useState(false);
  const [result, setResult]       = useState(null);

  const handleUpload = async () => {
    if (!file || !propId) { toast.error("Select a property and a CSV file"); return; }
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    form.append("property_id", propId);
    try {
      const { data } = await api.post("/bookings/import-csv", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      onImported();
    } catch (err) { toast.error(err.response?.data?.detail || err.message || "Import failed"); }
    finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/30 backdrop-blur-sm">
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md border border-slate-100 dark:border-slate-700 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-slate-800 dark:text-white text-sm">Import Booking History</h2>
          <button onClick={onClose}><X size={16} className="text-slate-400" /></button>
        </div>

        {result ? (
          <div className="text-center py-6 space-y-2">
            <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto">
              <CheckCircle size={28} weight="fill" className="text-green-500" />
            </div>
            <p className="font-semibold text-slate-800 dark:text-white">Import complete</p>
            <p className="text-sm text-slate-500">{result.imported} bookings imported, {result.skipped} skipped</p>
            <p className="text-xs text-slate-400">Your AI pricing suggestions will now use this historical data.</p>
            <button onClick={onClose} className="mt-2 px-5 py-2 bg-amber-400 text-white text-sm font-semibold rounded-xl hover:bg-amber-500 transition">Done</button>
          </div>
        ) : (
          <>
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-xs text-amber-800 space-y-1">
              <p className="font-semibold">Supports Airbnb CSV export</p>
              <p>Go to Airbnb → Reservations → Export CSV. Also accepts Booking.com and generic CSVs with check-in, check-out, and amount columns.</p>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1 block">Property</label>
              <select value={propId} onChange={e => setPropId(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-400">
                {properties.map(p => (
                  <option key={p.property_id || p.id} value={p.property_id || p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1 block">CSV File</label>
              <input type="file" accept=".csv" onChange={e => setFile(e.target.files[0])}
                className="w-full text-sm text-slate-600 dark:text-slate-300 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-amber-50 file:text-amber-600 hover:file:bg-amber-100 cursor-pointer" />
            </div>

            <div className="flex gap-3 justify-end pt-1">
              <button onClick={onClose} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-800 transition">Cancel</button>
              <button onClick={handleUpload} disabled={uploading || !file}
                className="flex items-center gap-2 px-5 py-2 bg-amber-400 hover:bg-amber-500 text-white text-sm font-semibold rounded-xl transition disabled:opacity-50">
                {uploading ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <><UploadSimple size={14} weight="bold" /> Import</>}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}

export default function Pricing() {
  const { selectedPropertyId } = usePropertyFilter();
  const [properties, setProperties]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [suggestAllTick, setSuggestAllTick] = useState(0);
  const [suggestingAll, setSuggestingAll]   = useState(false);
  const [showCsvModal, setShowCsvModal]     = useState(false);
  const [seasonalRules, setSeasonalRules]           = useState([]);
  const [savingSeasonalRules, setSavingSeasonalRules] = useState(false);

  const loadData = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const dayOfMonth = now.getDate();
    const monthStart = new Date(year, month, 1);
    const periodEnd  = new Date(year, month, dayOfMonth + 1);

    Promise.all([
      api.get("/properties").then(r => r.data),
      api.get("/bookings").then(r => r.data).catch(() => []),
    ]).then(([props, bookings]) => {
      const allProps    = Array.isArray(props)    ? props    : [];
      const allBookings = Array.isArray(bookings) ? bookings : [];
      const withOccupancy = allProps.map(p => {
        const propId = p.property_id || p.id;
        const propBookings = allBookings.filter(b => b.property_id === propId);
        let bookedDays = 0;
        for (const b of propBookings) {
          if (!b.check_in || !b.check_out) continue;
          const checkIn  = new Date(b.check_in);
          const checkOut = new Date(b.check_out);
          const start = checkIn  < monthStart ? monthStart : checkIn;
          const end   = checkOut > periodEnd  ? periodEnd  : checkOut;
          if (end > start) bookedDays += Math.round((end - start) / 86400000);
        }
        const roomCount = p.unit_type === "hotel" ? Math.max(p.rooms?.length || 1, 1) : 1;
        const occupancy_pct = dayOfMonth > 0 ? Math.min(100, Math.round((bookedDays / (dayOfMonth * roomCount)) * 100)) : 0;
        return { ...p, occupancy_pct };
      });
      setProperties(withOccupancy);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    api.get("/settings/pricing")
      .then(r => setSeasonalRules(r.data?.seasonal_rules || []))
      .catch(() => {});
  }, []);

  const handleRateUpdate = (propId, newRate) => {
    setProperties(ps => ps.map(p => (p.property_id || p.id) === propId ? { ...p, nightly_rate: newRate } : p));
  };

  const handleMinMaxUpdate = (propId, min, max) => {
    setProperties(ps => ps.map(p =>
      (p.property_id || p.id) === propId ? { ...p, min_price: min, max_price: max } : p
    ));
  };

  const saveSeasonalRules = async () => {
    setSavingSeasonalRules(true);
    try {
      await api.put("/settings/pricing", { seasonal_rules: seasonalRules });
      toast.success("Seasonal rules saved");
    } catch { toast.error("Failed to save seasonal rules"); }
    finally { setSavingSeasonalRules(false); }
  };

  const handleSuggestAll = () => {
    setSuggestingAll(true);
    setSuggestAllTick(t => t + 1);
    setTimeout(() => setSuggestingAll(false), 2000);
  };

  const filteredProperties = selectedPropertyId
    ? properties.filter(p => (p.property_id || p.id) === selectedPropertyId)
    : properties;

  const totalRevenuePotential = filteredProperties.reduce((sum, p) => {
    return sum + Math.round(((p.occupancy_pct ?? 0) / 100) * 30 * (p.nightly_rate || 0));
  }, 0);

  return (
    <div className="space-y-6">
      <OnboardingBanner pageKey="pricing" />
      <PricingAlertsCard />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-manrope text-2xl font-bold text-slate-900 dark:text-amber-400 tracking-tight">Dynamic Pricing</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">AI-powered nightly rate suggestions using live market data and your booking history.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {totalRevenuePotential > 0 && (
            <div className="text-right">
              <p className="text-[11px] text-slate-400">Est. Monthly Revenue</p>
              <p className="text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-0.5 justify-end">
                <CurrencyInr size={18} weight="bold" />
                {totalRevenuePotential.toLocaleString("en-IN")}
              </p>
            </div>
          )}
          <button onClick={() => setShowCsvModal(true)}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 transition">
            <UploadSimple size={13} weight="bold" /> Import History
          </button>
          <button onClick={handleSuggestAll} disabled={suggestingAll || properties.length === 0}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-xl bg-amber-400 hover:bg-amber-500 text-white transition disabled:opacity-50">
            {suggestingAll
              ? <div className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              : <Sparkle size={13} weight="fill" />}
            Suggest All
          </button>
        </div>
      </div>

      {/* Seasonal Rules */}
      <SeasonalRulesPanel
        rules={seasonalRules}
        onChange={setSeasonalRules}
        onSave={saveSeasonalRules}
        saving={savingSeasonalRules}
      />

      {/* How it works */}
      <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-800 rounded-xl p-4 flex gap-3">
        <Sparkle size={16} className="text-amber-500 shrink-0 mt-0.5" weight="fill" />
        <div className="text-xs text-amber-800 dark:text-amber-300 space-y-1">
          <p className="font-semibold">How AI pricing works</p>
          <p>Suggestions use five layers: <strong>live web search</strong> for current competitor rates, <strong>your booking history</strong> for seasonal patterns, <strong>India market benchmarks</strong> (avg ADR &amp; occupancy by city/month), <strong>demand events</strong> (festivals, IPL, school holidays), and a <strong>30-day rate calendar</strong> with weekend premiums, last-minute discounts, and gap-fill pricing baked in.</p>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[1, 2].map(i => <div key={i} className="h-48 skeleton rounded-2xl" />)}
        </div>
      ) : properties.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Buildings size={36} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No properties yet. Add a property first to use dynamic pricing.</p>
        </div>
      ) : filteredProperties.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Buildings size={36} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No pricing data for the selected property.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <AnimatePresence>
            {filteredProperties.map(p => (
              <PropertyPricingCard
                key={p.property_id || p.id}
                property={p}
                onRateUpdate={handleRateUpdate}
                onMinMaxUpdate={handleMinMaxUpdate}
                autoSuggest={suggestAllTick > 0}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      {showCsvModal && (
        <CsvImportModal
          properties={properties}
          onClose={() => setShowCsvModal(false)}
          onImported={loadData}
        />
      )}
    </div>
  );
}
